import {createFileRoute, notFound} from "@tanstack/react-router";
import {z as zod} from "zod";
import {DAG_ConnectionAttemptStatus} from "~/domainModels/dag/dataSourceConnectionAttempt";
import {dataSourceConnectionStrategyDetailsMap} from "~/domainModels/dag/dataSourceConnectionStrategy";
import {getDataSourcesForFrontend} from "~/domainModels/dag/dataSources";
import {getLinkedInOAuthRedirectUri} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottosync/_ottosyncScaffold/data-sources/$dataSourceId/-utilities";
import {
    redirectToConnectionPage,
    ThirdPartyPlatformIds,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/oauth-callback/-utilities";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/oauth-callback/9265d2ea-fa77-4827-9918-f377c01dd17c",
)({
    beforeLoad: async ({context, search}) => {
        // #region Get connectionAttemptId
        const connectionAttemptIdResult = uuidZodValidator.safeParse(search["state"]);
        if (connectionAttemptIdResult.success !== true) {
            // Throwing error is fine as we are sure that this field should exist even in the case of failure
            throw connectionAttemptIdResult.error;
        }
        const connectionAttemptId = connectionAttemptIdResult.data;
        // #endregion

        // #region Get authorization code
        const authorizationCodeResult = zod.string().safeParse(search["code"]);

        // #region Handling User Cancelled OAuth case
        if (authorizationCodeResult.success !== true) {
            const connectionAttempt = context.connectionAttemptClient.getConnectionAttempt(connectionAttemptId);
            if (connectionAttempt === undefined) {
                throw new Error("Connection attempt not found");
            }

            connectionAttempt.status = DAG_ConnectionAttemptStatus.failed;
            const setConnectionAttemptFailedResult = context.connectionAttemptClient.setConnectionAttempt(connectionAttempt);
            if (setConnectionAttemptFailedResult.success !== true) {
                throw setConnectionAttemptFailedResult.error;
            }

            redirectToConnectionPage({
                connectionAttempt: connectionAttempt,
            });
        }
        // #endregion

        const authorizationCode = authorizationCodeResult.data;
        // #endregion

        // #region Deduplicate requests
        // Having to do this due to stupid reasons. Ref 8cffe1bb-927b-4f3d-9cf8-a4ac74ddcbe9
        if (window._oauthCallbackDeduplicationSet === undefined) {
            window._oauthCallbackDeduplicationSet = new Set();
        }
        const _oauthCallbackDeduplicationSet: Set<string> = window._oauthCallbackDeduplicationSet;
        if (_oauthCallbackDeduplicationSet.has(`${connectionAttemptId}/${authorizationCode}`) === true) {
            return;
        }
        _oauthCallbackDeduplicationSet.add(`${connectionAttemptId}/${authorizationCode}`);
        // #endregion

        // #region Get connectionAttempt
        const connectionAttempt = context.connectionAttemptClient.getConnectionAttempt(connectionAttemptId);
        if (connectionAttempt === undefined) {
            throw new Error("Connection attempt not found");
        }
        // #endregion

        const dataSourcesResult = await getDataSourcesForFrontend({
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            workspaceId: connectionAttempt.workspaceId,
            filters: {
                id: connectionAttempt.dataSourceId,
            },
        });
        if (dataSourcesResult.success !== true) {
            throw dataSourcesResult.error;
        }
        const dataSources = dataSourcesResult.data;
        if (dataSources.length === 0) {
            throw notFound();
        }
        const dataSource = dataSources[0]!;

        const dataSourceConnectionStrategyDetails = dataSourceConnectionStrategyDetailsMap[dataSource.connectionStrategy];

        // #region Exchange code for refereshToken
        const exchangeAuthCodeResult = await robustJsonResultClientFetch(
            `${context.intellsysBaseUrl}/sub-system/xolotl/ui/data-sources/connection-strategies/exchange-auth-code`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                    "X-Workspace-Id": connectionAttempt.workspaceId,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    connectionStrategyId: ThirdPartyPlatformIds.linkedIn,
                    payload: {
                        authorizationCode: authorizationCode,
                        connectionStrategy: dataSource.connectionStrategy,
                        redirectUri: getLinkedInOAuthRedirectUri(context.intellsysBaseUrl),
                    },
                }),
            },
            zod.object({
                refreshToken: zod.string(),
            }),
        );
        if (exchangeAuthCodeResult.success !== true) {
            // TODO: isn't it better to mark the connection attempt as failed?
            throw exchangeAuthCodeResult.error;
        }
        const {refreshToken} = exchangeAuthCodeResult.data;
        // #endregion

        // #region Update information in connectionAttempt
        connectionAttempt.stepData[connectionAttempt.currentStepId] = {
            refreshToken: refreshToken,
        };
        // #endregion

        // #region Move connectionAttempt to next step

        const currentStepIndex = dataSourceConnectionStrategyDetails.connectionSteps.findIndex((step) => step.id === connectionAttempt.currentStepId);
        if (currentStepIndex === -1) {
            throw new Error("Invalid state");
        }
        const nextStepIndex = currentStepIndex + 1;
        if (nextStepIndex >= dataSourceConnectionStrategyDetails.connectionSteps.length) {
            throw new Error("Invalid state");
        }
        const nextStep = dataSourceConnectionStrategyDetails.connectionSteps[nextStepIndex]!;

        connectionAttempt.currentStepId = nextStep.id;
        connectionAttempt.status = DAG_ConnectionAttemptStatus.inProgress;
        // #endregion

        // #region Save connectionAttempt
        const setConnectionAttemptResult = context.connectionAttemptClient.setConnectionAttempt(connectionAttempt);
        if (setConnectionAttemptResult.success !== true) {
            throw setConnectionAttemptResult.error;
        }
        // #endregion

        // #region Send user back to connection page
        redirectToConnectionPage({
            connectionAttempt: connectionAttempt,
        });
        // #endregion
    },
    loader: async () => {},
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
